import axios from 'axios'
import { message, Modal, notification } from 'ant-design-vue'
import moment from 'moment'
import store from '../store'
import db from 'utils/localstorage'

moment.locale('zh-cn')

const FEBS_REQUEST = axios.create({
  baseURL: '/api',
  responseType: 'json',
  validateStatus (status) {
    return status === 200
  }
})

FEBS_REQUEST.interceptors.request.use(
  (config) => {
    const expireTime = store.state.account.expireTime
    const now = moment().format('YYYYMMDDHHmmss')
    if (expireTime && now - expireTime >= -10) {
      Modal.error({
        title: '登录已过期',
        content: '很抱歉，登录已过期，请重新登录',
        okText: '重新登录',
        mask: false,
        onOk: () => {
          return new Promise(() => {
            db.clear()
            location.reload()
          })
        }
      })
    }
    if (store.state.account.token) {
      config.headers.Authentication = store.state.account.token
    }
    return config
  },
  (error) => Promise.reject(error)
)

FEBS_REQUEST.interceptors.response.use(
  (config) => config,
  (error) => {
    if (error.config && error.config.hideError) {
      return Promise.reject(error)
    }
    if (error.response) {
      const errorMessage = error.response.data
        ? error.response.data.msg
        : '系统内部异常，请联系网站管理员'
      switch (error.response.status) {
        case 404:
          notification.error({
            message: '系统提示',
            description: '很抱歉，资源未找到',
            duration: 4
          })
          break
        case 403:
        case 401:
          Modal.error({
            title: '系统提示',
            content: '很抱歉，您无法访问该资源，可能是因为没有相应权限或者登录已失效',
            okText: '重新登录',
            mask: false,
            onOk: () => {
              return new Promise(() => {
                db.clear()
                location.reload()
              })
            }
          })
          break
        default:
          notification.error({
            message: '系统提示',
            description: errorMessage,
            duration: 4
          })
          break
      }
    } else {
      message.error('网络异常，请稍后重试')
    }
    return Promise.reject(error)
  }
)

const request = {
  post (url, params) {
    return FEBS_REQUEST.post(url, params, {
      transformRequest: [
        (params) => {
          let result = ''
          Object.keys(params).forEach((key) => {
            if (!Object.is(params[key], undefined) && !Object.is(params[key], null)) {
              result += encodeURIComponent(key) + '=' + encodeURIComponent(params[key]) + '&'
            }
          })
          return result
        }
      ],
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    })
  },
  post2 (url, params) {
    return FEBS_REQUEST.post(url, params, {
      headers: {
        'Content-Type': 'application/json'
      }
    })
  },
  get (url, params, config) {
    let _params = ''
    if (!Object.is(params, undefined)) {
      _params = '?'
      for (const key in params) {
        if (
          Object.prototype.hasOwnProperty.call(params, key) &&
          params[key] !== null &&
          params[key] !== undefined &&
          params[key] !== ''
        ) {
          _params += `${key}=${params[key]}&`
        }
      }
    }
    return FEBS_REQUEST.get(`${url}${_params}`, config)
  },
  put (url, params) {
    return FEBS_REQUEST.put(url, params, {
      transformRequest: [
        (params) => {
          let result = ''
          Object.keys(params).forEach((key) => {
            if (!Object.is(params[key], undefined) && !Object.is(params[key], null)) {
              result += encodeURIComponent(key) + '=' + encodeURIComponent(params[key]) + '&'
            }
          })
          return result
        }
      ],
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    })
  },
  put2(url, params) {
    return FEBS_REQUEST.put(url, params, {
      headers: {
        'Content-Type': 'application/json',
      },
    });
  },
  delete (url, params) {
    let _params = ''
    if (!Object.is(params, undefined)) {
      _params = '?'
      for (const key in params) {
        if (Object.prototype.hasOwnProperty.call(params, key) && params[key] !== null) {
          _params += `${key}=${params[key]}&`
        }
      }
    }
    return FEBS_REQUEST.delete(`${url}${_params}`)
  },
  delete2 (url, params) {
    return FEBS_REQUEST.delete(url, { data: params })
  },
  export (url, params = {}, filename = `${new Date().getTime()}_导出结果`) {
    return FEBS_REQUEST.post(url, params, {
      transformRequest: [
        (params) => {
          let result = ''
          Object.keys(params).forEach((key) => {
            if (
              Object.prototype.hasOwnProperty.call(params, key) &&
              params[key] !== null &&
              params[key] !== undefined &&
              params[key] !== ''
            ) {
              result += encodeURIComponent(key) + '=' + encodeURIComponent(params[key]) + '&'
            }
          })
          return result
        }
      ],
      responseType: 'blob',
      hideError: true
    })
      .then((r) => {
        message.loading('导出数据中')
        const content = r.data
        if (content.size <= 53) {
          message.destroy()
          message.error('没有导出记录')
          return
        }
        const blob = new Blob([content])
        const fileName = `${filename}.xlsx`
        const elink = document.createElement('a')
        elink.download = fileName
        elink.style.display = 'none'
        elink.href = URL.createObjectURL(blob)
        document.body.appendChild(elink)
        elink.click()
        URL.revokeObjectURL(elink.href)
        document.body.removeChild(elink)
      })
      .catch((r) => {
        const reader = new FileReader()
        reader.onload = function () {
          try {
            const responseData = JSON.parse(reader.result)
            if (responseData.msg) {
              message.error(responseData.msg)
            }
          } catch (e) {
            message.error('导出失败')
          }
        }
        if (r.request && r.request.response) {
          reader.readAsText(r.request.response)
        } else {
          message.error('导出失败')
        }
      })
  },
  exportQuery (url, params = {}, filename = `${new Date().getTime()}_导出结果`) {
    let query = '?'
    for (const key in params) {
      if (
        Object.prototype.hasOwnProperty.call(params, key) &&
        params[key] !== null &&
        params[key] !== undefined &&
        params[key] !== ''
      ) {
        query += `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}&`
      }
    }
    const urlWithQuery = query === '?' ? url : `${url}${query}`
    return FEBS_REQUEST.post(urlWithQuery, {}, {
      headers: {
        'Content-Type': 'application/json',
      },
      responseType: 'blob',
      hideError: true,
    })
      .then((r) => {
        message.loading('导出数据中')
        const content = r.data
        if (content.size <= 53) {
          message.destroy()
          message.error('没有导出记录')
          return
        }
        const blob = new Blob([content])
        const fileName = `${filename}.xlsx`
        const elink = document.createElement('a')
        elink.download = fileName
        elink.style.display = 'none'
        elink.href = URL.createObjectURL(blob)
        document.body.appendChild(elink)
        elink.click()
        URL.revokeObjectURL(elink.href)
        document.body.removeChild(elink)
      })
      .catch((r) => {
        const reader = new FileReader()
        reader.onload = function () {
          try {
            const responseData = JSON.parse(reader.result)
            if (responseData.msg) {
              message.error(responseData.msg)
            }
          } catch (e) {
            message.error('导出失败')
          }
        }
        if (r.request && r.request.response) {
          reader.readAsText(r.request.response)
        } else {
          message.error('导出失败')
        }
      })
  },
}

export default request
